import React, { useMemo, useState } from 'react'

interface TabItem {
    label: string
    content: React.ReactNode
}

interface TabsProps {
    tabs: TabItem[]
    unmountInactiveTabs?: boolean
    activeIndex?: number
    initialActiveIndex?: number
    onTabChange?: (index: number) => void
}

const Tabs: React.FC<TabsProps> = ({
    tabs,
    unmountInactiveTabs = false,
    activeIndex: controlledActiveIndex,
    initialActiveIndex = 0,
    onTabChange,
}) => {
    const isControlled = useMemo(
        () => controlledActiveIndex !== undefined,
        [controlledActiveIndex],
    )
    const [uncontrolledIndex, setUncontrolledIndex] =
        useState(initialActiveIndex)

    const activeIndex = isControlled
        ? (controlledActiveIndex as number)
        : uncontrolledIndex

    return (
        <div className="w-full">
            {/* Tab Headers */}
            <div className="flex border-b">
                {tabs.map((tab, index) => (
                    <button
                        key={index}
                        className={`px-4 py-2 -mb-px border-b-2 font-medium text-sm ${
                            activeIndex === index
                                ? 'border-ctxt-blue text-ctxt-blue'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                        onClick={() => {
                            if (isControlled) {
                                onTabChange?.(index)
                            } else {
                                setUncontrolledIndex(index)
                                onTabChange?.(index)
                            }
                        }}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>
            {/* Tab Content */}
            <div className="mt-4">
                {unmountInactiveTabs ? (
                    // Only render the active tab; other tabs are unmounted
                    <div key={activeIndex}>{tabs[activeIndex].content}</div>
                ) : (
                    // Render all tabs; hide inactive ones but keep them mounted
                    tabs.map((tab, index) => (
                        <div
                            key={index}
                            className={activeIndex === index ? '' : 'hidden'}
                        >
                            {tab.content}
                        </div>
                    ))
                )}
            </div>
        </div>
    )
}

export default Tabs
